import { useLanguage } from '../../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import paymentService from '../../services/payment.service';
import LoadingSpinner from '../../components/common/LoadingSpinner';

// Initialize Stripe (use env var in production)
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

// ==============================
// Stripe Card Form Component
// ==============================
const StripeCardForm = ({ orderIds, summary, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      // Process payment for each order
      for (const orderId of orderIds) {
        // 1. Create payment intent on server
        const { data: intentData } = await paymentService.createStripeIntent(orderId);

        // 2. Confirm payment with Stripe
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
          intentData.clientSecret,
          {
            payment_method: {
              card: elements.getElement(CardElement),
            },
          }
        );

        if (stripeError) {
          setError(stripeError.message);
          setProcessing(false);
          return;
        }

        // 3. Confirm on server
        if (paymentIntent.status === 'succeeded') {
          await paymentService.confirmStripePayment(paymentIntent.id);
        }
      }

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#1F2937',
        fontFamily: 'Inter, sans-serif',
        '::placeholder': { color: '#9CA3AF' },
      },
      invalid: { color: '#EF4444' },
    },
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4 p-3 border rounded bg-white">
        <label className="form-label fw-semibold mb-2">{t('cardDetails')}</label>
        <CardElement options={cardStyle} />
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center gap-2 py-2">
          <FiAlertCircle /> {error}
        </div>
      )}

      <button
        type="submit"
        className="btn btn-fd-primary btn-lg w-100"
        disabled={!stripe || processing}
      >
        {processing ? 'Processing Payment...' : `Pay $${summary?.totalAmount?.toFixed(2) || '0.00'}`}
      </button>

      <p className="text-center text-muted small mt-2">
        Powered by <strong>Stripe</strong> · Encrypted & Secure
      </p>
    </form>
  );
};

// ==============================
// Razorpay Button Component
// ==============================
const RazorpayButton = ({ orderIds, summary, onSuccess }) => {
  const [processing, setProcessing] = useState(false);

  const handleRazorpayPayment = async () => {
    setProcessing(true);

    try {
      for (const orderId of orderIds) {
        // 1. Create Razorpay order on server
        const { data } = await paymentService.createRazorpayOrder(orderId);

        // 2. Open Razorpay checkout
        await new Promise((resolve, reject) => {
          const options = {
            key: data.keyId,
            amount: data.amount,
            currency: data.currency,
            order_id: data.razorpayOrderId,
            name: 'FarmDirectly',
            description: 'Order Payment',
            handler: async (response) => {
              try {
                // 3. Verify on server
                await paymentService.verifyRazorpayPayment({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                });
                resolve();
              } catch (err) {
                reject(err);
              }
            },
            modal: {
              ondismiss: () => reject(new Error('Payment cancelled')),
            },
            theme: { color: '#B91C1C' },
          };

          const rzp = new window.Razorpay(options);
          rzp.open();
        });
      }

      onSuccess();
    } catch (err) {
      if (err.message !== 'Payment cancelled') {
        toast.error(err.response?.data?.message || 'Payment failed');
      }
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <button
        className="btn btn-fd-primary btn-lg w-100"
        onClick={handleRazorpayPayment}
        disabled={processing}
      >
        {processing ? 'Processing...' : `Pay $${summary?.totalAmount?.toFixed(2) || '0.00'} with Razorpay`}
      </button>
      <p className="text-center text-muted small mt-2">
        Powered by <strong>Razorpay</strong> · UPI, Cards, Netbanking, Wallets
      </p>
    </div>
  );
};

// ==============================
// Main Payment Page
// ==============================
const PaymentPage = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { orderIds, orders, summary, paymentMethod } = location.state || {};
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    if (!orderIds || orderIds.length === 0) {
      navigate('/cart');
    }
  }, [orderIds, navigate]);

  // Dynamically load Razorpay script
  useEffect(() => {
    if (paymentMethod === 'razorpay') {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
      return () => document.body.removeChild(script);
    }
  }, [paymentMethod]);

  const handleSuccess = () => {
    setPaymentSuccess(true);
    window.scrollTo(0, 0);
  };

  if (!orderIds) return <LoadingSpinner />;

  if (paymentSuccess) {
    return (
      <div className="section-padding text-center">
        <div className="container" style={{ maxWidth: '500px' }}>
          <FiCheckCircle size={72} className="text-success mb-3" />
          <h2 className="mb-3">{t('paymentSuccessful')}</h2>
          <p className="text-muted mb-1">
            Your order{orderIds.length > 1 ? 's have' : ' has'} been confirmed.
          </p>
          <p className="text-muted mb-4">
            Order{orderIds.length > 1 ? 's' : ''}: {orders?.map((o) => o.orderNumber).join(', ')}
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <Link to="/account/orders" className="btn btn-fd-primary">{t('viewMyOrders')}</Link>
            <Link to="/products" className="btn btn-fd-outline">{t('continueShopping')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-padding bg-light" style={{ minHeight: '70vh' }}>
      <div className="container" style={{ maxWidth: '550px' }}>
        <h3 className="text-center mb-4">{t('completePayment')}</h3>

        {/* Order Summary */}
        <div className="dashboard-card mb-4">
          <div className="d-flex justify-content-between mb-2">
            <span>{t('subtotal')}</span>
            <span>${summary?.subtotal?.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span>{t('tax')}</span>
            <span>${summary?.tax?.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span>{t('shipping')}</span>
            <span>{summary?.shippingCost === 0 ? 'FREE' : `$${summary?.shippingCost?.toFixed(2)}`}</span>
          </div>
          <hr />
          <div className="d-flex justify-content-between">
            <strong className="fs-5">{t('total')}</strong>
            <strong className="fs-5 text-primary">${summary?.totalAmount?.toFixed(2)}</strong>
          </div>
        </div>

        {/* Payment Form */}
        <div className="dashboard-card">
          {paymentMethod === 'stripe' ? (
            <Elements stripe={stripePromise}>
              <StripeCardForm
                orderIds={orderIds}
                summary={summary}
                onSuccess={handleSuccess}
              />
            </Elements>
          ) : (
            <RazorpayButton
              orderIds={orderIds}
              summary={summary}
              onSuccess={handleSuccess}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
